/**
 * Парсинг координат из ссылки 2GIS
 *
 * Поддерживаемые форматы ссылок:
 * - https://2gis.kz/shymkent/branches/70000001041483076/firm/70000001101191920?m=69.587036%2C42.315722%2F10.7
 * - https://2gis.kz/almaty?m=76.889709,43.238949/15
 *
 * Параметр m= содержит: longitude,latitude/zoom
 */

export interface Coordinates {
  longitude: number;
  latitude: number;
}

/**
 * Извлекает координаты из ссылки 2GIS
 * @param url - ссылка из 2GIS
 * @returns объект с координатами или null если не удалось распарсить
 */
export function parse2GisLink(url: string): Coordinates | null {
  if (!url || typeof url !== 'string') {
    return null;
  }

  try {
    // Декодируем URL-encoded символы
    const decodedUrl = decodeURIComponent(url);

    // Ищем параметр m= в URL
    // Может быть в query string (?m=) или в хэше (#m=)
    const mParamMatch = decodedUrl.match(/[?&#]m=([^&\s]+)/);

    if (!mParamMatch || !mParamMatch[1]) {
      return null;
    }

    const mValue = mParamMatch[1];

    // Формат: longitude,latitude/zoom или longitude,latitude
    // Примеры: 69.587036,42.315722/10.7 или 76.889709,43.238949
    const coordsMatch = mValue.match(/^(-?\d+\.?\d*),(-?\d+\.?\d*)/);

    if (!coordsMatch) {
      return null;
    }

    const longitude = parseFloat(coordsMatch[1]);
    const latitude = parseFloat(coordsMatch[2]);

    // Валидация диапазонов
    if (!isValidLongitude(longitude) || !isValidLatitude(latitude)) {
      return null;
    }

    return { longitude, latitude };
  } catch {
    return null;
  }
}

/**
 * Проверяет валидность долготы
 * @param longitude - значение долготы
 * @returns true если longitude в диапазоне [-180, 180]
 */
export function isValidLongitude(longitude: number): boolean {
  return !isNaN(longitude) && longitude >= -180 && longitude <= 180;
}

/**
 * Проверяет валидность широты
 * @param latitude - значение широты
 * @returns true если latitude в диапазоне [-90, 90]
 */
export function isValidLatitude(latitude: number): boolean {
  return !isNaN(latitude) && latitude >= -90 && latitude <= 90;
}
